import { z } from 'zod';

const HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const CTA_HREF = /^(https:\/\/|\/)/;
const HTTPS = /^https:\/\//;

export const MAX_ITEMS = 20;
export const MAX_CTAS = 3;
export const MAX_RAIL_PRODUCTS = 50;
export const RAIL_LIMIT_MAX = 48;
export const RAIL_LIMIT_DEFAULT = 12;
export const CUSTOM_BAG_MAX_BYTES = 16_384;

export const contentStatuses = ['draft', 'active'] as const;
export const blockKinds = ['banner', 'rail'] as const;
export const blockScopes = ['store', 'shared'] as const;
export const mediaKinds = ['image', 'video'] as const;
export const ctaLinkTypes = ['url', 'product', 'collection'] as const;
export const railSourceTypes = ['collection', 'products'] as const;
export const customValueTypes = ['string', 'number', 'boolean', 'json'] as const;

export type BlockKind = (typeof blockKinds)[number];
export type BlockScope = (typeof blockScopes)[number];

/** Route param of the create editor: `/blocks/new/$kind`. */
export const blockKindParamSchema = z.enum(blockKinds);

/**
 * One row of the custom bag as typed: the value is always a string and `type` says how the
 * mapper parses it (number, boolean, JSON) before it travels as JSON.
 */
export const customEntrySchema = z
  .object({
    key: z
      .string()
      .trim()
      .min(1, { error: 'content:validation.customKeyRequired' })
      .max(64, { error: 'content:validation.customKeyLength' }),
    type: z.enum(customValueTypes),
    value: z.string(),
  })
  .superRefine((entry, ctx) => {
    if (
      entry.type === 'number' &&
      (entry.value.trim() === '' || Number.isNaN(Number(entry.value)))
    ) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: 'content:validation.customNumber' });
    }
    if (entry.type === 'json') {
      try {
        JSON.parse(entry.value);
      } catch {
        ctx.addIssue({ code: 'custom', path: ['value'], message: 'content:validation.customJson' });
      }
    }
  });

export const customBagFormSchema = z.array(customEntrySchema).superRefine((entries, ctx) => {
  const seen = new Set<string>();
  entries.forEach((entry, index) => {
    if (seen.has(entry.key)) {
      ctx.addIssue({
        code: 'custom',
        path: [index, 'key'],
        message: 'content:validation.customKeyDuplicate',
      });
    }
    seen.add(entry.key);
  });
});

/** A library pick keeps the id and an optional alt override; a URL carries everything itself. */
export const mediaRefFormSchema = z
  .discriminatedUnion('type', [
    z.object({
      type: z.literal('library'),
      mediaId: z.string().min(1, { error: 'content:validation.mediaRequired' }),
      alt: z.string().trim().max(300, { error: 'content:validation.altLength' }),
    }),
    z.object({
      type: z.literal('url'),
      url: z
        .string()
        .trim()
        .min(1, { error: 'content:validation.urlRequired' })
        .max(2048)
        .regex(HTTPS, { error: 'content:validation.urlHttps' }),
      kind: z.enum(mediaKinds),
      alt: z
        .string()
        .trim()
        .min(1, { error: 'content:validation.altRequired' })
        .max(300, { error: 'content:validation.altLength' }),
      width: z.string().trim().regex(/^\d*$/, { error: 'content:validation.dimension' }),
      height: z.string().trim().regex(/^\d*$/, { error: 'content:validation.dimension' }),
    }),
  ])
  .nullable();

/** Flat on purpose: every link type keeps its own field so switching type loses nothing. */
export const ctaFormSchema = z
  .object({
    label: z
      .string()
      .trim()
      .min(1, { error: 'content:validation.ctaLabelRequired' })
      .max(80, { error: 'content:validation.ctaLabelLength' }),
    linkType: z.enum(ctaLinkTypes),
    url: z.string().trim().max(2048),
    productId: z.string(),
    /** Display only; the mapper drops it. */
    productTitle: z.string(),
    collectionId: z.string(),
    openInNewTab: z.boolean(),
    style: z.string().trim().max(40, { error: 'content:validation.ctaStyleLength' }),
  })
  .superRefine((cta, ctx) => {
    if (cta.linkType === 'url' && !CTA_HREF.test(cta.url)) {
      ctx.addIssue({ code: 'custom', path: ['url'], message: 'content:validation.ctaUrl' });
    }
    if (cta.linkType === 'product' && cta.productId === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['productId'],
        message: 'content:validation.ctaProduct',
      });
    }
    if (cta.linkType === 'collection' && cta.collectionId === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['collectionId'],
        message: 'content:validation.ctaCollection',
      });
    }
  });

const bannerItemBase = z.object({
  media: mediaRefFormSchema,
  mobileMedia: mediaRefFormSchema,
  eyebrow: z.string().trim().max(120, { error: 'content:validation.eyebrowLength' }),
  title: z.string().trim().max(200, { error: 'content:validation.titleLength' }),
  subtitle: z.string().trim().max(200, { error: 'content:validation.subtitleLength' }),
  descriptionHtml: z.string().trim().max(20_000, { error: 'content:validation.descriptionLength' }),
  ctas: z.array(ctaFormSchema).max(MAX_CTAS, { error: 'content:validation.ctasMax' }),
  custom: customBagFormSchema,
});

/** A slide needs something to show; a rail header may stay empty and is then dropped. */
export const bannerItemFormSchema = bannerItemBase.refine(
  (item) => item.media !== null || item.title !== '' || item.descriptionHtml !== '',
  { path: ['title'], error: 'content:validation.itemEmpty' },
);

export const railHeaderFormSchema = bannerItemBase;

/** Display fields travel with the id so the list renders without a lookup; the mapper keeps only the id. */
export const railProductFormSchema = z.object({
  productId: z.string(),
  title: z.string(),
  handle: z.string(),
  status: z.string(),
});

/** What every block has, whatever its kind. Spread into the two kind schemas. */
const blockCommonSchema = {
  key: z
    .string()
    .trim()
    .min(1, { error: 'content:validation.keyRequired' })
    .max(120)
    .regex(HANDLE, { error: 'content:validation.keyFormat' }),
  name: z
    .string()
    .trim()
    .min(1, { error: 'content:validation.nameRequired' })
    .max(120, { error: 'content:validation.nameLength' }),
  status: z.enum(contentStatuses),
  /** datetime-local strings, empty when unset. */
  startsAt: z.string(),
  endsAt: z.string(),
  custom: customBagFormSchema,
};

function checkSchedule(block: { startsAt: string; endsAt: string }, ctx: z.RefinementCtx) {
  if (block.startsAt && block.endsAt && new Date(block.startsAt) >= new Date(block.endsAt)) {
    ctx.addIssue({
      code: 'custom',
      path: ['endsAt'],
      message: 'content:validation.endsAfterStarts',
    });
  }
}

export const bannerFormSchema = z
  .object({
    kind: z.literal('banner'),
    ...blockCommonSchema,
    items: z
      .array(bannerItemFormSchema)
      .min(1, { error: 'content:validation.itemsRequired' })
      .max(MAX_ITEMS, { error: 'content:validation.itemsMax' }),
  })
  .superRefine(checkSchedule);

/**
 * Scope is not part of the schema: the shared editor never offers the collection source and
 * `emptyRailForm('shared')` starts on hand-picked products. The backend's 422 on
 * `body.rail.source.type` is mapped onto `sourceType` as a safety net.
 */
export const railFormSchema = z
  .object({
    kind: z.literal('rail'),
    ...blockCommonSchema,
    header: railHeaderFormSchema,
    sourceType: z.enum(railSourceTypes),
    collectionId: z.string(),
    products: z.array(railProductFormSchema).max(MAX_RAIL_PRODUCTS, {
      error: 'content:validation.railProductsMax',
    }),
    limit: z.string().trim(),
  })
  .superRefine((block, ctx) => {
    if (block.sourceType === 'collection' && block.collectionId === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['collectionId'],
        message: 'content:validation.railCollection',
      });
    }
    if (block.sourceType === 'products' && block.products.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['products'],
        message: 'content:validation.railProducts',
      });
    }
    const limit = Number(block.limit);
    if (!/^\d+$/.test(block.limit) || limit < 1 || limit > RAIL_LIMIT_MAX) {
      ctx.addIssue({
        code: 'custom',
        path: ['limit'],
        message: 'content:validation.railLimit',
      });
    }
    checkSchedule(block, ctx);
  });

/** The kind is chosen before the editor opens, so one form only ever holds one branch. */
export const blockFormSchema = z.discriminatedUnion('kind', [bannerFormSchema, railFormSchema]);

export type BannerFormInput = z.input<typeof bannerFormSchema>;
export type BannerFormOutput = z.output<typeof bannerFormSchema>;
export type RailFormInput = z.input<typeof railFormSchema>;
export type RailFormOutput = z.output<typeof railFormSchema>;
export type BlockFormInput = z.input<typeof blockFormSchema>;
export type BlockFormOutput = z.output<typeof blockFormSchema>;
export type BannerItemForm = z.infer<typeof bannerItemFormSchema>;
export type RailHeaderForm = z.infer<typeof railHeaderFormSchema>;
export type CtaForm = z.infer<typeof ctaFormSchema>;
export type MediaRefForm = z.infer<typeof mediaRefFormSchema>;
export type CustomEntryForm = z.infer<typeof customEntrySchema>;
export type RailProductForm = z.infer<typeof railProductFormSchema>;

/**
 * Backend field paths follow the API body (`banner.items.0.media`, `rail.source.type`); the
 * form is flat per kind. Used through `useApplyFieldErrors` so a 422 lands on the right field.
 */
export function apiFieldToFormField(field: string): string {
  if (field.startsWith('banner.items')) return field.replace(/^banner\./, '');
  if (field === 'rail.source.type') return 'sourceType';
  if (field === 'rail.source.collectionId') return 'collectionId';
  if (field.startsWith('rail.source.product')) return 'products';
  if (field.startsWith('rail.')) return field.replace(/^rail\./, '');
  return field;
}
